/**
 * TanStack Query cache keys.
 *
 * Design §1.4 lists a `TanStackQueryKeys` helper as something `@crm/api-client`
 * "ships". It does not, today — the package exports `createCrmClient`, the auth
 * adapters and the retry policy, and nothing else. These live here until it
 * does; moving them is a re-export, and mobile is the second consumer that will
 * force the question.
 *
 * **Every key starts with the tenant** (design §2.3). Switching org must not
 * show the previous tenant's rows for even one frame, and a key that omits the
 * org id makes that impossible to guarantee — the cache would happily serve the
 * old tenant's `['contacts','list',{}]` entry to the new one. `'unscoped'` is
 * used before the session resolves, so pre-auth data can never collide with a
 * real tenant's.
 */

export type ContactListFilters = {
  readonly type?: string | undefined;
  readonly listId?: string | undefined;
  readonly source: string;
};

export type TimelineFilters = {
  readonly contactId: string;
};

/** The subset of `events.list` the server honours. Verified in `queries.ts`. */
export type EventListFilters = {
  readonly q?: string | undefined;
  readonly kind?: string | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
};

function scope(orgId: string | null | undefined): readonly ['crm', string] {
  return ['crm', orgId ?? 'unscoped'] as const;
}

export const queryKeys = {
  session: () => ['session'] as const,

  orgs: (orgId?: string | null) => [...scope(orgId), 'orgs'] as const,

  contacts: (orgId?: string | null) => [...scope(orgId), 'contacts'] as const,
  contactList: (orgId: string | null | undefined, filters: ContactListFilters) =>
    [...scope(orgId), 'contacts', 'list', filters] as const,
  contactDetail: (orgId: string | null | undefined, contactId: string) =>
    [...scope(orgId), 'contacts', 'detail', contactId] as const,

  interactions: (orgId?: string | null) => [...scope(orgId), 'interactions'] as const,
  timeline: (orgId: string | null | undefined, filters: TimelineFilters) =>
    [...scope(orgId), 'interactions', 'timeline', filters] as const,
  feed: (orgId?: string | null) => [...scope(orgId), 'interactions', 'feed'] as const,

  lists: (orgId?: string | null) => [...scope(orgId), 'lists'] as const,
  listSearch: (orgId: string | null | undefined, q: string) =>
    [...scope(orgId), 'lists', 'search', q] as const,
  listDetail: (orgId: string | null | undefined, listId: string) =>
    [...scope(orgId), 'lists', 'detail', listId] as const,

  events: (orgId?: string | null) => [...scope(orgId), 'events'] as const,
  eventList: (orgId: string | null | undefined, filters: EventListFilters) =>
    [...scope(orgId), 'events', 'list', filters] as const,
  eventDetail: (orgId: string | null | undefined, eventId: string) =>
    [...scope(orgId), 'events', 'detail', eventId] as const,
  eventsCurrent: (orgId?: string | null) => [...scope(orgId), 'events', 'current'] as const,

  /** Promised intros and asks (design §1.10), per contact and org-wide. */
  commitments: (orgId?: string | null) => [...scope(orgId), 'commitments'] as const,
  commitmentsFor: (orgId: string | null | undefined, contactId: string | null) =>
    [...scope(orgId), 'commitments', 'list', contactId ?? 'all'] as const,

  /** Paired Macs, phones and extensions. */
  devices: (orgId?: string | null) => [...scope(orgId), 'devices'] as const,

  /**
   * Hermes Chat. `chatSessions` and `chatHistory` share the `chat` prefix
   * because a turn changes both: a new session appears in the list, and an
   * existing one's `lastMessageAt` and `messageCount` move.
   */
  chat: (orgId?: string | null) => [...scope(orgId), 'chat'] as const,
  chatSessions: (orgId?: string | null) => [...scope(orgId), 'chat', 'sessions'] as const,
  chatHistory: (orgId: string | null | undefined, sessionId: string) =>
    [...scope(orgId), 'chat', 'history', sessionId] as const,

  /**
   * Sharing. `outgoing` and `incoming` nest under one `shares` prefix because
   * every write touches both sides of the same relationship: revoking an
   * outgoing grant removes it from the grantee's incoming list, and adopting an
   * incoming one changes what the owner sees. One prefix, one invalidation.
   */
  shares: (orgId?: string | null) => [...scope(orgId), 'shares'] as const,
  sharesOutgoing: (orgId: string | null | undefined, resourceType: string | null) =>
    [...scope(orgId), 'shares', 'outgoing', resourceType ?? 'all'] as const,
  sharesIncoming: (
    orgId: string | null | undefined,
    filters: { readonly resourceType: string | null; readonly unadoptedOnly: boolean },
  ) => [...scope(orgId), 'shares', 'incoming', filters] as const,

  /**
   * Enrichment. The dry-run estimate is keyed per contact and NOT persisted
   * long: it reports what a run would cost right now, and a stale credit
   * estimate is a lie about money.
   */
  enrichment: (orgId?: string | null) => [...scope(orgId), 'enrichment'] as const,
  enrichmentEstimate: (orgId: string | null | undefined, contactId: string) =>
    [...scope(orgId), 'enrichment', 'estimate', contactId] as const,
  enrichmentHistory: (orgId: string | null | undefined, contactId: string) =>
    [...scope(orgId), 'enrichment', 'history', contactId] as const,

  /**
   * The review queue. `reviewList` nests under `review` so resolving one item
   * can invalidate the whole queue (and its counts) with a single prefix — every
   * decision changes both the page and the badge.
   */
  review: (orgId?: string | null) => [...scope(orgId), 'review'] as const,
  reviewList: (orgId: string | null | undefined, kinds: readonly string[]) =>
    [...scope(orgId), 'review', 'list', [...kinds].sort()] as const,
  reviewCounts: (orgId?: string | null) => [...scope(orgId), 'review', 'counts'] as const,

  /**
   * Email threads. `threadMessages` and `messageBody` nest under `messages` so
   * one prefix invalidates a thread and everything opened inside it — a new
   * message arriving changes the list, the summary AND which bodies exist.
   */
  messages: (orgId?: string | null) => [...scope(orgId), 'messages'] as const,
  threadMessages: (orgId: string | null | undefined, threadId: string) =>
    [...scope(orgId), 'messages', 'thread', threadId] as const,
  messageBody: (orgId: string | null | undefined, messageId: string) =>
    [...scope(orgId), 'messages', 'body', messageId] as const,

  orgSettings: (orgId: string | null | undefined) => [...scope(orgId), 'org-settings'] as const,

  /**
   * Meetings. Everything on `/meetings/:id` nests under one `meetings` prefix so
   * a single `transcript_ready` frame — or one speaker assignment — invalidates
   * the transcript, the summary, the highlights and the speaker list together.
   * They are four reads of one object, and showing three of them refreshed while
   * the fourth is stale is worse than refetching all four.
   */
  meetings: (orgId?: string | null) => [...scope(orgId), 'meetings'] as const,
  meetingTranscript: (orgId: string | null | undefined, meetingId: string) =>
    [...scope(orgId), 'meetings', 'transcript', meetingId] as const,
  meetingSummary: (orgId: string | null | undefined, meetingId: string) =>
    [...scope(orgId), 'meetings', 'summary', meetingId] as const,
  meetingHighlights: (orgId: string | null | undefined, meetingId: string) =>
    [...scope(orgId), 'meetings', 'highlights', meetingId] as const,
  meetingSpeakers: (orgId: string | null | undefined, meetingId: string) =>
    [...scope(orgId), 'meetings', 'speakers', meetingId] as const,

  /** The privacy screen's suppression list. */
  suppressions: (orgId?: string | null) => [...scope(orgId), 'suppressions'] as const,
  suppressionList: (orgId: string | null | undefined, flag: string | null) =>
    [...scope(orgId), 'suppressions', 'list', flag ?? 'all'] as const,

  /**
   * Hermes. `digest`, `followUps` and `drafts` are three prefixes rather than
   * one because they invalidate at different moments: sending a draft changes
   * the draft AND the digest item's outcome AND the follow-up's state, while
   * snoozing changes only the last two. One prefix would make every action
   * refetch the whole morning.
   */
  digest: (orgId?: string | null) => [...scope(orgId), 'digest'] as const,
  digestForDate: (orgId: string | null | undefined, localDate: string | null) =>
    [...scope(orgId), 'digest', 'date', localDate ?? 'latest'] as const,
  followUps: (orgId?: string | null) => [...scope(orgId), 'follow-ups'] as const,
  drafts: (orgId?: string | null) => [...scope(orgId), 'drafts'] as const,
  hermesProfile: (orgId?: string | null) => [...scope(orgId), 'hermes-profile'] as const,
  hermesMemories: (orgId?: string | null) => [...scope(orgId), 'hermes-memories'] as const,
  llmSpend: (orgId: string | null | undefined, periodMonth: string | null) =>
    [...scope(orgId), 'ops', 'llm-spend', periodMonth ?? 'current'] as const,

  /** Connected provider accounts + their sync-health dashboard (design §2.9). */
  syncAccounts: (orgId?: string | null) => [...scope(orgId), 'sync-accounts'] as const,
} as const;
